/**
 * Product Analysis Page
 *
 * Main page for AI-powered creative analysis with a collapsible sidebar.
 * Mirrors the Antares WeeklyReport/CreativesInsight layout.
 *
 * Sidebar tabs:
 *  - Performance One Pager
 *  - Creatives Insight
 *  - Fatigue
 *  - Ad Opportunities
 *  - Next Steps
 */

import { useState, useMemo } from 'react'
import * as Icons from 'lucide-react'
import { MetaConnectionGuard } from '@/shared/components/MetaConnectionGuard'
import { PerformanceOnePager } from './analysis/PerformanceOnePager'
import { FatigueTab } from './analysis/FatigueTab'
import { CreativesInsightTab } from './analysis/CreativesInsightTab'
import { AdOpportunitiesTab } from './analysis/AdOpportunitiesTab'
import { NextStepsTab } from './analysis/NextStepsTab'
import { ANALYSIS_SCREENS } from './analysis/types'
import type { AnalysisScreenId } from './analysis/types'

// ============================================================================
// Date helpers
// ============================================================================

function getDateRange(days: number) {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - days)
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  }
}

function formatDateDisplay(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ============================================================================
// Icon resolver
// ============================================================================

function ScreenIcon({ name, className }: { name: string; className?: string }) {
  const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    TrendingUp: Icons.TrendingUp,
    Battery: Icons.Battery,
    Lightbulb: Icons.Lightbulb,
    Target: Icons.Target,
    Wand2: Icons.Wand2,
  }
  const Icon = iconMap[name] || Icons.FileBarChart
  return <Icon className={className} />
}

// ============================================================================
// Main Component
// ============================================================================

function ProductAnalysisContent() {
  const [activeScreen, setActiveScreen] = useState<AnalysisScreenId>('performance')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [datePreset, setDatePreset] = useState<'7D' | '14D' | '30D'>('7D')

  const dateRange = useMemo(() => {
    const days = datePreset === '7D' ? 7 : datePreset === '14D' ? 14 : 30
    return getDateRange(days)
  }, [datePreset])

  const currentScreen = ANALYSIS_SCREENS.find((s) => s.id === activeScreen) || ANALYSIS_SCREENS[0]

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icons.FileBarChart className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl font-bold text-tf-text">Product Analysis</h1>
        </div>

        {/* Date Range + Controls */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-tf-text-muted">
            {formatDateDisplay(dateRange.startDate)} — {formatDateDisplay(dateRange.endDate)}
          </span>
          <div className="flex bg-tf-bg-hover rounded-lg p-1">
            {(['7D', '14D', '30D'] as const).map((preset) => (
              <button
                key={preset}
                onClick={() => setDatePreset(preset)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  datePreset === preset
                    ? 'bg-tf-bg-card text-tf-text shadow-sm'
                    : 'text-tf-text-muted hover:bg-tf-bg-card hover:text-tf-text'
                }`}
              >
                {preset}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Layout: Sidebar + Content */}
      <div className="flex gap-4">
        {/* Collapsible Sidebar */}
        <div
          className={`flex flex-col gap-1 rounded-xl border border-tf-border bg-tf-bg-card px-3 py-4 transition-all duration-300 ${
            sidebarOpen ? 'min-w-[220px]' : 'w-[60px] items-center'
          }`}
        >
          {/* Toggle button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`flex items-center p-2 rounded-lg text-tf-text-muted hover:bg-tf-feedback-info-bg hover:text-blue-600 transition-colors mb-1 ${sidebarOpen ? 'justify-start self-start' : 'justify-center'}`}
          >
            {sidebarOpen ? (
              <Icons.PanelRightClose className="w-5 h-5" />
            ) : (
              <Icons.PanelLeftClose className="w-5 h-5" />
            )}
          </button>

          {/* Screen buttons */}
          {ANALYSIS_SCREENS.filter((s) => s.isActive).map((screen) => (
            <button
              key={screen.id}
              onClick={() => setActiveScreen(screen.id)}
              title={screen.title}
              className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                activeScreen === screen.id
                  ? 'bg-blue-600 text-white'
                  : 'text-tf-text-muted hover:bg-tf-bg-hover hover:text-tf-text'
              } ${sidebarOpen ? 'justify-start' : 'justify-center'}`}
            >
              <ScreenIcon
                name={screen.icon}
                className="w-4 h-4 flex-shrink-0"
              />
              {sidebarOpen && (
                <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                  {screen.title}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-1 rounded-xl border border-tf-border bg-tf-bg-card p-5 min-h-[600px]">
          {/* Content Header */}
          <div className="flex items-center gap-2 mb-5 pb-3 border-b border-tf-border-sub">
            <ScreenIcon name={currentScreen.icon} className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-tf-text">{currentScreen.title}</h2>
          </div>

          {/* Active Screen */}
          {activeScreen === 'performance' && (
            <PerformanceOnePager startDate={dateRange.startDate} endDate={dateRange.endDate} />
          )}
          {activeScreen === 'insight' && (
            <CreativesInsightTab startDate={dateRange.startDate} endDate={dateRange.endDate} />
          )}
          {activeScreen === 'fatigue' && (
            <FatigueTab startDate={dateRange.startDate} endDate={dateRange.endDate} />
          )}
          {activeScreen === 'opportunities' && (
            <AdOpportunitiesTab startDate={dateRange.startDate} endDate={dateRange.endDate} />
          )}
          {activeScreen === 'next-steps' && (
            <NextStepsTab startDate={dateRange.startDate} endDate={dateRange.endDate} />
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Export with Connection Guard
// ============================================================================

export default function ProductAnalysisPage() {
  return (
    <MetaConnectionGuard
      title="Connect Meta Ads Account"
      description="Connect your Meta Ads account to view product analysis and creative insights."
      icon="FileBarChart"
    >
      <ProductAnalysisContent />
    </MetaConnectionGuard>
  )
}
